import { useEffect, RefObject } from 'react';

export type UseControlledPopoverParams = {
  controlledOpen?: boolean;
  forceClosed?: number;
  display: string;
  mountContent: boolean;
  hasOpenedAtLeastOnce: boolean;
  previousState: RefObject<'open' | 'closed'>;
  handleOpen: () => void;
  handleClose: () => void;
};

/**
 * Handle controlled component behavior and forced close.
 * Manages synchronization between controlled open prop and internal state.
 */
export function useControlledPopover({
  controlledOpen,
  forceClosed,
  display,
  mountContent,
  hasOpenedAtLeastOnce,
  previousState,
  handleOpen,
  handleClose,
}: UseControlledPopoverParams): void {
  useEffect(() => {
    if (forceClosed) {
      handleClose();
    }
  }, [forceClosed, handleClose]);

  // Call handleOpen/handleClose when controlled component based on open prop (controlledOpen)
  useEffect(() => {
    if (controlledOpen !== undefined) {
      if (controlledOpen) {
        if (previousState.current === 'closed') {
          handleOpen();
        }
      } else {
        if (display === 'block' && mountContent && hasOpenedAtLeastOnce && previousState.current === 'open') {
          handleClose();
        }
      }
    }
  }, [controlledOpen, display, handleClose, handleOpen, hasOpenedAtLeastOnce, mountContent, previousState]);
}
